/**
 * Shared color label + hex swatch logic (admin + storefront).
 * Hex codes stored as the “color name” match the product photo shade; labels are human-readable.
 */

export function normalizeHexColor(value) {
  if (typeof value !== 'string') return '';
  const v = value.trim();
  return /^#[0-9A-Fa-f]{6}$/.test(v) ? v.toUpperCase() : '';
}

/**
 * Lenient hex for variant matching: #RGB, #RRGGBB, optional #, 3- or 6-digit.
 */
export function normalizeHexFlexible(value) {
  if (value == null) return '';
  let v = String(value).trim();
  if (!v) return '';
  if (!v.startsWith('#')) {
    if (/^[0-9A-Fa-f]{6}$/i.test(v)) v = `#${v}`;
    else if (/^[0-9A-Fa-f]{3}$/i.test(v)) {
      const [r, g, b] = v;
      v = `#${r}${r}${g}${g}${b}${b}`;
    } else return '';
  }
  const u = v.toUpperCase();
  if (/^#[0-9A-F]{6}$/.test(u)) return u;
  if (/^#[0-9A-F]{3}$/.test(u)) {
    const x = u.slice(1);
    return `#${x[0]}${x[0]}${x[1]}${x[1]}${x[2]}${x[2]}`;
  }
  return '';
}

/**
 * @param {string} colorName — stored key (#RRGGBB or name)
 * @param {string[]} orderedProductColors — full color list for this product
 * @returns Label for buttons (e.g. "As image", "As image 2")
 */
export function getColorDisplayName(colorName, orderedProductColors) {
  const hex = normalizeHexColor(String(colorName));
  if (!hex) return String(colorName);
  const list = Array.isArray(orderedProductColors)
    ? orderedProductColors.filter((c) => normalizeHexColor(String(c)))
    : [];
  const idx = list.findIndex((c) => normalizeHexColor(String(c)) === hex);
  if (list.length <= 1) return 'As image';
  return `As image ${idx >= 0 ? idx + 1 : list.length}`;
}

/** Hide placeholder variant keys from admin checkboxes (internal stock may still use them). */
export function isPlaceholderColorKey(c) {
  return String(c ?? '')
    .trim()
    .toLowerCase() === 'default';
}

export function isPlaceholderSizeKey(s) {
  const x = String(s ?? '')
    .trim()
    .toLowerCase();
  return x === 'default' || x === 'default size';
}

/** Palette rows for #RRGGBB color names stored on products (custom picks). */
export function extractHexPaletteFromColorNames(names) {
  if (!Array.isArray(names)) return [];
  const seen = new Set();
  const out = [];
  for (const n of names) {
    const h = normalizeHexColor(String(n));
    if (h && !seen.has(h)) {
      seen.add(h);
      out.push({ name: h, code: h });
    }
  }
  return out;
}

const STOREFRONT_NAME_TO_HEX = {
  'As image': '#9E9E9E',
  'Default color': '#9E9E9E',
  Golden: '#D4AF37',
  'Light Gold': '#F0E68C',
  'Dark Gold': '#B8860B',
  'Cream Gold': '#F5DEB3',
  'Golden Yellow': '#FFD700',
  'Golden Beige': '#E6D2AA',
  'Antique Gold': '#C9A227',
  Gold: '#FFD700',
  Cream: '#F5F5DC',
  White: '#FFFFFF',
  Black: '#000000',
  Navy: '#000080',
  'Navy Blue': '#000080',
  Beige: '#F5F5DC',
  Brown: '#A0522D',
  Gray: '#808080',
  Grey: '#808080',
  Silver: '#C0C0C0',
  Blue: '#4169E1',
  'Light Blue': '#ADD8E6',
  'Sky Blue': '#87CEEB',
  'Royal Blue': '#4169E1',
  Turquoise: '#40E0D0',
  Teal: '#008080',
  Pink: '#FFC0CB',
  'Rose Pink': '#FF69B4',
  'Dusty Pink': '#DCAE96',
  Peach: '#FFCBA4',
  'Light Pink': '#FFB6C1',
  Green: '#008000',
  'Sage Green': '#9CAF88',
  Emerald: '#50C878',
  Mint: '#98FB98',
  Purple: '#800080',
  Red: '#FF0000',
  Ruby: '#E0115F',
  Maroon: '#800000',
  Orange: '#FFA500',
  Yellow: '#FFFF00',
  Ivory: '#FFFFF0',
  Nude: '#E3C4A8',
  Champagne: '#F7E7CE',
  'Rose Gold': '#E8B4B8',
};

/**
 * CSS `background` hex for product card dots and storefront swatches.
 * Uses backend hex strings as-is; maps common names; never uses random hues for unknown strings.
 */
export function getStorefrontSwatchHex(input) {
  const colorString =
    typeof input === 'string'
      ? input.trim()
      : String(
          input && typeof input === 'object'
            ? input.name || input.value || input.color || ''
            : input ?? ''
        ).trim();
  if (!colorString) return '#9E9E9E';

  const flex = normalizeHexFlexible(colorString);
  if (flex) return flex;

  if (STOREFRONT_NAME_TO_HEX[colorString]) {
    return STOREFRONT_NAME_TO_HEX[colorString];
  }
  const lowerKeyed = colorString.toLowerCase();
  const mapKeyInsensitive = Object.keys(STOREFRONT_NAME_TO_HEX).find(
    (k) => k.toLowerCase() === lowerKeyed
  );
  if (mapKeyInsensitive) return STOREFRONT_NAME_TO_HEX[mapKeyInsensitive];

  const lower = lowerKeyed;
  if (lower.includes('print') || lower.includes('pattern')) {
    if (lower.includes('blue')) return '#4169E1';
    if (lower.includes('red')) return '#DC143C';
    if (lower.includes('green')) return '#228B22';
    if (lower.includes('pink')) return '#FFB6C1';
    if (lower.includes('black')) return '#2F2F2F';
    if (lower.includes('floral')) return '#FFB6C1';
    return '#9370DB';
  }
  if (lower.includes('blue')) return '#4169E1';
  if (lower.includes('red')) return '#FF0000';
  if (lower.includes('green')) return '#008000';
  if (lower.includes('pink')) return '#FFC0CB';
  if (lower.includes('yellow')) return '#FFFF00';
  if (lower.includes('orange')) return '#FFA500';
  if (lower.includes('purple')) return '#800080';
  if (lower.includes('brown')) return '#A0522D';
  if (lower.includes('black')) return '#000000';
  if (lower.includes('white')) return '#FFFFFF';
  if (lower.includes('gray') || lower.includes('grey')) return '#808080';
  if (lower.includes('gold')) return '#D4AF37';
  if (lower.includes('silver')) return '#C0C0C0';
  if (lower.includes('cream')) return '#F5F5DC';
  if (lower.includes('beige')) return '#F5F5DC';
  if (lower.includes('navy')) return '#000080';
  if (lower.includes('antique')) return '#C9A227';

  return '#BDBDBD';
}
